import React, { Component } from 'react';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogTitle,
  DialogContent,
  FormGroup,
  FormControlLabel,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableRow,
  TableCell,
  Input
} from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import PropTypes from 'prop-types';
import SelectReact from 'react-select';
import isEmail from 'validator/lib/isEmail';
import { isWithinInterval } from 'date-fns';
import ContractInputModal from './ContractInputModal';
import ContractSummary from './ContractSummary';
import {
  findAgentOptions,
  formatPositionOptions,
  mapAgentEntities,
  mapOriginEntities,
} from '../../helpers/optionFormatters';
import {
  addEmployee,
  fetchAgencies,
  fetchAgents,
  fetchOrigins,
  fetchPositions,
} from '../../helpers/externalRequests';
import { formatDateOnlyStringFromLocalTime, groupAgents } from '../../helpers/helpers';
import i18n from '../../i18n';
import countryCodes from '../../constants/countryCodes';
import { UserType } from '../../validation';
import RequireScope from '../RequireScope';

import '../../assets/stylesheets/AvailabilityBox.css';
import '../../assets/stylesheets/EmployeeAttributes.css';

class AddEmployeeModal extends Component {
  constructor(props) {
    super(props);
    const nationalities = this.formatNationalities();
    this.state = {
      isOpen: false,
      inputFields: this.getInitialInputFieldsState(),
      nationalities,
      origins: [],
      positions: [],
      groupedAgents: []
    };
    this.manageContractsModalRef = React.createRef();
  }

  componentDidMount = () => {
    this.fetchAllData();
  };

  determineActivityFromContracts = (contracts, today) => {
    let isActive = false;
    const date = today || new Date();
    contracts.forEach(contract => {
      if (
        isWithinInterval(date, {
          start: contract.contractStart,
          end: contract.contractEnd
        })
      ) {
        isActive = true;
      }
    });
    return isActive;
  };

  formatNationalities = () =>
    countryCodes.map((countryCode, index) => {
      const i18nName = i18n.t(`country:${countryCode}`);
      return {
        id: index,
        name: i18nName,
        label: i18nName,
        value: countryCode,
        isSelected: false
      };
    });

  resetIsSelected = items =>
    items.map(item => {
      return {
        ...item,
        isSelected: false
      };
    });

  fetchAllData = async () => {
    const { displaySnackbar } = this.props;
    try {
      const [origins, positions, agencies, agents] = await Promise.all(
        [
          fetchOrigins(),
          fetchPositions(),
          fetchAgencies(),
          fetchAgents()
        ]
      );
      const formattedPositions = formatPositionOptions(positions.data, { isSelected: false });
      const groupedAgents = groupAgents(agencies.data, agents.data.map(mapAgentEntities));
      const formattedOrigins = origins.data.origins.map(mapOriginEntities);
      this.setState({
        origins: formattedOrigins,
        positions: formattedPositions,
        groupedAgents
      });
    } catch (error) {
      displaySnackbar(`${i18n.t('errorRetrievingData')} ${error}`);
    };
  };

  formatContractsForSave = contracts =>
    contracts.map(contract =>
      ({
        ...contract,
        contractStart: formatDateOnlyStringFromLocalTime(
          contract.contractStart
        ),
        contractEnd: formatDateOnlyStringFromLocalTime(
          contract.contractEnd
        ),
      })
    );

  getInitialInputFieldsState = () => {
    return {
      firstName: '',
      lastName: '',
      nickName: '',
      email: '@ssense.com',
      homeNumber: '',
      workNumber: '',
      isActive: true,
      isPOPreApproved: false,
      isFullTime: true,
      isContractor: false,
      agentIds: [],
      contracts: [],
      nationalityCountryCodes: [],
      originIds: [],
      positionIds: [],
      primaryPositionId: null,
      errors: {
        firstName: false,
        lastName: false,
        nickName: false,
        email: false,
        homeNumber: false,
        workNumber: false
      }
    };
  };

  handleAgentChange = event => {
    this.setState(prevState => {
      return ({
        inputFields: {
        ...prevState.inputFields,
        agentIds: event ? event.map(value => value.id) : []
        }
      })});
  };

  handleChange = event => {
    event.persist();
    const fieldName = event.currentTarget.name;
    const fieldValue = event.target.value;
    this.setState(prevState => ({
      inputFields: {
        ...prevState.inputFields,
        [fieldName]: fieldValue
      }
    }));
  };

  handleCheckboxToggle = (checkboxName, value) => {
    if (checkboxName === 'isContractor' && value === true) {
      // a contractor is automatically full time
      this.handleCheckboxToggle('isFullTime', true);
    }
    if (checkboxName === 'isContractor' && value === false) {
      this.manageContractsModalRef.current.resetDialog();
    }
    if (checkboxName === 'isFullTime' && value === false) {
      // a contractor must be full time
      this.handleCheckboxToggle('isContractor', false);
    }
    this.setState(prevState => ({
      inputFields: {
        ...prevState.inputFields,
        [checkboxName]: value
      }
    }));
  };

  handleOriginChange = arrayOfSelectedItems => {
    this.setState(prevState => {
      const prevOrigins = JSON.parse(JSON.stringify(prevState.origins));
      const originIds = [];
      const origins = arrayOfSelectedItems
        ? prevOrigins.map(origin => {
            const matchingSelection = arrayOfSelectedItems.find(
              selectedItem => selectedItem.id === origin.id
            );
            if (matchingSelection) {
              originIds.push(matchingSelection.id);
              return {
                ...origin,
                isSelected: true
              };
            }
            return {
              ...origin,
              isSelected: false
            };
          })
        : prevOrigins.map(origin => {
            return {
              ...origin,
              isSelected: false
            };
          });
      return {
        origins,
        inputFields: {
          ...prevState.inputFields,
          originIds
        }
      };
    });
  };

  handleNationalityChange = arrayOfSelectedItems => {
    this.setState(prevState => {
      const prevNationalities = JSON.parse(
        JSON.stringify(prevState.nationalities)
      );
      const nationalityCountryCodes = [];
      const nationalities = arrayOfSelectedItems
        ? prevNationalities.map(nationality => {
            const matchingSelection = arrayOfSelectedItems.find(
              selectedItem => selectedItem.id === nationality.id
            );
            if (matchingSelection) {
              nationalityCountryCodes.push(matchingSelection.value);
              return {
                ...nationality,
                isSelected: true
              };
            }
            return {
              ...nationality,
              isSelected: false
            };
          })
        : prevNationalities.map(nationality => {
            return {
              ...nationality,
              isSelected: false
            };
          });
      return {
        nationalities,
        inputFields: {
          ...prevState.inputFields,
          nationalityCountryCodes
        }
      };
    });
  };

  handlePositionChange = arrayOfSelectedItems => {
    this.setState(
      prevState => {
        const prevPositions = JSON.parse(JSON.stringify(prevState.positions));
        const positionIds = [];
        let updatedPrimaryPositionId = arrayOfSelectedItems
          ? prevState.inputFields.primaryPositionId
          : null;
        const positions = arrayOfSelectedItems
          ? prevPositions.map(position => {
              const matchingSelection = arrayOfSelectedItems.find(
                selectedItem => selectedItem.id === position.id
              );
              if (matchingSelection) {
                if (positionIds.length === 0) {
                  updatedPrimaryPositionId = matchingSelection.id;
                }
                positionIds.push(matchingSelection.id);
                return {
                  ...position,
                  isSelected: true
                };
              }
              if (prevState.inputFields.primaryPositionId === position.id) {
                updatedPrimaryPositionId = null;
              }
              return {
                ...position,
                isSelected: false
              };
            })
          : prevPositions.map(position => {
              return {
                ...position,
                isSelected: false
              };
            });
        return {
          positions,
          inputFields: {
            ...prevState.inputFields,
            positionIds,
            primaryPositionId: updatedPrimaryPositionId
          }
        };
      },
      () => {
        const { inputFields } = this.state;
        this.manageContractsModalRef.current.updateContracts(
          inputFields.positionIds
        )
      }
    );
  };

  handlePrimaryPositionChange = selectedItem => {
    if (selectedItem) {
      this.setState(prevState => {
        const updatedInputFields = {
          ...prevState.inputFields,
          primaryPositionId: selectedItem.id
        };
        return { inputFields: updatedInputFields };
      });
    }
  };

  handleSave = async () => {
    const { inputFields } = this.state;
    const { displaySnackbar, refreshEmployees } = this.props;
    const errors = {
      firstName: false,
      lastName: false,
      nickName: false,
      email: false,
      homeNumber: false,
      workNumber: false
    };
    if (inputFields.firstName === '') {
      errors.firstName = true;
    }
    if (inputFields.lastName === '') {
      errors.lastName = true;
    }

    let validEmail = false;
    validEmail = isEmail(inputFields.email);
    if (validEmail && inputFields.isFullTime) {
      const pos = inputFields.email.indexOf('@ssense.com');
      if (pos + '@ssense.com'.length !== inputFields.email.length)
        validEmail = false;
    }
    if (!validEmail) {
      errors.email = true;
    }
    if (errors.firstName || errors.lastName || errors.email) {
      return this.setState(prevState => {
        const updatedInputFields = {
          ...prevState.inputFields,
          errors
        };
        return { inputFields: updatedInputFields };
      });
    }
    const employee = {
      ...inputFields
    };

    const formattedContracts = inputFields.contracts.length
      ? this.formatContractsForSave(inputFields.contracts)
      : [];

    try {
      await addEmployee(employee, formattedContracts);
      displaySnackbar(i18n.t('employeeCreateSuccess'));
      refreshEmployees();
      const initialInputFields = this.getInitialInputFieldsState();
      return this.setState(
        prevState => {
          const nationalities = this.resetIsSelected(prevState.nationalities);
          const origins = this.resetIsSelected(prevState.origins);
          const positions = this.resetIsSelected(prevState.positions);
          return {
            isOpen: false,
            nationalities,
            origins,
            positions,
            inputFields: initialInputFields
          };
        },
        () => this.manageContractsModalRef.current.resetDialog()
      );
    } catch (error) {
      const msg = error.response?.data?.message || error.message
      return displaySnackbar(`${i18n.t('employeeCreateError')} ${msg}`);
    };
  };

  handleSaveContracts = contracts => {
    contracts.forEach(contract =>
      this.updateSelectedPositions(contract.positionId)
    );
    const isActive = this.determineActivityFromContracts(contracts);
    this.setState(prevState => {
      return {
        inputFields: {
          ...prevState.inputFields,
          contracts,
          isActive
        }
      };
    });
  };

  hideDialog = () => {
    const inputFields = this.getInitialInputFieldsState();
    this.setState({ isOpen: false, inputFields });
    this.manageContractsModalRef.current.resetDialog();
  };

  showDialog = () => {
    this.setState(prevState => {
      const nationalities = this.resetIsSelected(prevState.nationalities);
      const origins = this.resetIsSelected(prevState.origins);
      const positions = this.resetIsSelected(prevState.positions);
      return {
        nationalities,
        origins,
        positions,
        isOpen: true
      };
    });
  };

  openContractModal = () => {
    this.manageContractsModalRef.current.showDialog();
  };

  updateContracts = contracts => {
    const updatedContracts = contracts.filter(contract =>
      contract.positionId !== 0
    );
    this.setState(prevState => {
      return {
        inputFields: {
          ...prevState.inputFields,
          contracts: updatedContracts
        }
      };
    });
  };

  updateSelectedPositions = id => {
    this.setState(prevState => {
      const updatedPositionIds = [...prevState.inputFields.positionIds];
      const updatedPositions = [...prevState.positions];
      const matchingIndex = updatedPositions.findIndex(
        position => position.id === id
      );
      updatedPositions[matchingIndex].isSelected = true;
      if (!updatedPositionIds.includes(id)) {
        updatedPositionIds.push(id);
      }
      const updatedPrimaryPositionId = prevState.inputFields.primaryPositionId
        ? prevState.inputFields.primaryPositionId
        : id;
      const updatedInputFields = {
        ...prevState.inputFields,
        positionIds: updatedPositionIds,
        primaryPositionId: updatedPrimaryPositionId
      };

      return { positions: updatedPositions, inputFields: updatedInputFields };
    });
  };

  render() {
    const {
      isOpen,
      inputFields,
      groupedAgents,
      nationalities,
      origins,
      positions
    } = this.state;

    const { user } = this.props;

    const {
      agentIds,
      firstName,
      lastName,
      nickName,
      email,
      homeNumber,
      workNumber,
      isActive,
      isFullTime,
      isContractor,
      contracts,
      positionIds,
      primaryPositionId,
      errors,
      isPOPreApproved
    } = inputFields;

    const primaryPositionValue = primaryPositionId
      ? positions.find(position => position.id === primaryPositionId)
      : positions.find(position => position.id === positionIds[0]);

    
    return (
      <>
        <Dialog
          open={isOpen}
          onBackdropClick={this.hideDialog}
          fullWidth
          maxWidth="md"
        >
    
          <DialogTitle disableTypography>
            <h4>{i18n.t('addEmployee')}</h4>
            <IconButton
              aria-label="close"
              className="modalButtonClose"
              onClick={this.hideDialog}
            >
              <CloseIcon />
            </IconButton>
          </DialogTitle>
          
          <DialogContent scroll="body">
            <Table>
              <TableBody>
                <TableRow>
                  <TableCell className="attributeLabel">
                    {i18n.t('firstName')}
                  </TableCell>
                  <TableCell>
                    <Input
                      className="attributeValue"
                      name="firstName"
                      value={firstName}
                      onChange={this.handleChange}
                      error={errors.firstName}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="attributeLabel">
                    {i18n.t('lastName')}
                  </TableCell>
                  <TableCell>
                    <Input
                      className="attributeValue"
                      name="lastName"
                      value={lastName}
                      onChange={this.handleChange}
                      error={errors.lastName}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="attributeLabel">
                    {i18n.t('preferredName')}
                  </TableCell>
                  <TableCell>
                    <Input
                      className="attributeValue"
                      name="nickName"
                      value={nickName}
                      placeholder={i18n.t('optional')}
                      onChange={this.handleChange}
                      error={errors.nickName}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="attributeLabel">
                    {i18n.t('emailAddress')}
                  </TableCell>
                  <TableCell>
                    <Input
                      className="attributeValue"
                      name="email"
                      value={email}
                      onChange={this.handleChange}
                      error={errors.email}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="attributeLabel">
                    {i18n.t('homeNumber')}
                  </TableCell>
                  <TableCell>
                    <Input
                      className="attributeValue"
                      name="homeNumber"
                      value={homeNumber}
                      placeholder={i18n.t('optional')}
                      onChange={this.handleChange}
                      error={errors.homeNumber}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="attributeLabel">
                    {i18n.t('workNumber')}
                  </TableCell>
                  <TableCell>
                    <Input
                      className="attributeValue"
                      name="workNumber"
                      value={workNumber}
                      placeholder={i18n.t('optional')}
                      onChange={this.handleChange}
                      error={errors.workNumber}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="attributeLabel">
                    {i18n.t('positions')}
                  </TableCell>
                  <TableCell>
                    <SelectReact
                      isMulti
                      hideSelectedOptions
                      menuPlacement="auto"
                      value={positions.filter(
                        position => position.isSelected === true
                      )}
                      options={positions}
                      onChange={this.handlePositionChange}
                    />
                  </TableCell>
                </TableRow>
                {positionIds.length ? (
                  <TableRow>
                    <TableCell className="attributeLabel">
                      {i18n.t('primaryPosition')}
                    </TableCell>
                    <TableCell>
                      <SelectReact
                        menuPlacement="auto"
                        value={primaryPositionValue}
                        options={positions.filter(position =>
                          positionIds.includes(position.id)
                        )}
                        onChange={this.handlePrimaryPositionChange}
                        placeholder={
                          positions.find(
                            position => position.id === positionIds[0]
                          ).name
                        }
                      />
                    </TableCell>
                  </TableRow>
                ) : null}
                
                <TableRow>
                  <TableCell className="attributeLabel">
                    {i18n.t('isFullTime')}
                  </TableCell>
                  <TableCell>
                    <FormGroup row>
                      <FormControlLabel
                        // prettier-ignore
                        control={(
                          <Checkbox
                            checked={isFullTime}
                            color="secondary"
                            onChange={() =>
                              this.handleCheckboxToggle(
                                'isFullTime',
                                !isFullTime
                              )
                            }
                          />
                        )}
                      />
                    </FormGroup>
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="attributeLabel">
                    {i18n.t('isContractor')}
                  </TableCell>
                  <TableCell>
                    <Grid container>
                      <Grid item>
                        <FormGroup row>
                          <FormControlLabel
                            // prettier-ignore
                            control={(
                              <Checkbox
                                checked={isContractor}
                                color="secondary"
                                onChange={() =>
                                  this.handleCheckboxToggle(
                                    'isContractor',
                                    !isContractor
                                  )
                                }
                              />
                            )}
                          />
                        </FormGroup>
                      </Grid>
                      <Grid item>
                        <Button
                          className="ssenseButton ssenseSecondary"
                          onClick={this.openContractModal}
                          disabled={!isContractor}
                        >
                          {i18n.t('userProfile:manageContracts')}
                        </Button>
                      </Grid>
                    </Grid>
                  </TableCell>
                </TableRow>
                {contracts.length ? (
                  <ContractSummary
                    contracts={contracts}
                    positions={positions}
                  />
                ) : null}
                <TableRow>
                  <TableCell className="attributeLabel">
                    {i18n.t('agents')}
                  </TableCell>
                  <TableCell>
                    <SelectReact
                      isMulti
                      fullWidth
                      value={
                        groupedAgents
                          ? findAgentOptions(groupedAgents, agentIds)
                          : null
                      }
                      hideSelectedOptions
                      menuShouldScrollIntoView={false}
                      className="bookingSelectField"
                      options={groupedAgents}
                      onChange={this.handleAgentChange}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="attributeLabel">
                    {i18n.t('booking:nationalities')}
                  </TableCell>
                  <TableCell>
                    <SelectReact
                      isMulti
                      hideSelectedOptions
                      menuPlacement="auto"
                      value={nationalities.filter(
                        nationality => nationality.isSelected === true
                      )}
                      options={nationalities}
                      onChange={this.handleNationalityChange}
                    />
                  </TableCell>
                </TableRow>
                <TableRow>
                  <TableCell className="attributeLabel">
                    {i18n.t('booking:origins')}
                  </TableCell>
                  <TableCell>
                    <SelectReact
                      isMulti
                      hideSelectedOptions
                      menuPlacement="auto"
                      value={origins.filter(
                        origin => origin.isSelected === true
                      )}
                      options={origins}
                      onChange={this.handleOriginChange}
                    />
                  </TableCell>
                </TableRow>
                <RequireScope scopes={['po-pre-approved:read']}>
                  <TableRow>
                    <TableCell className="attributeLabel">
                      {i18n.t('isPOPreApproved')}
                    </TableCell>
                    <TableCell>
                      <FormGroup row>
                        <FormControlLabel
                          control={(
                            <Checkbox
                              checked={isPOPreApproved}
                              color="secondary"
                              disabled={!user.scopes.includes('po-pre-approved:write')}
                              onChange={() =>
                                this.handleCheckboxToggle('isPOPreApproved', !isPOPreApproved)
                              }
                            />
                          )}
                        />
                      </FormGroup>
                    </TableCell>
                  </TableRow>
                </RequireScope>
                <TableRow>
                  <TableCell className="attributeLabel">
                    {i18n.t('isActive')}
                  </TableCell>
                  <TableCell>
                    <FormGroup row>
                      <FormControlLabel
                        // prettier-ignore
                        control={(
                          <Checkbox
                            checked={isActive}
                            color="secondary"
                            // if this is a contratator, activity will
                            // be determined through contract dates
                            disabled={isContractor}
                            onChange={() =>
                              this.handleCheckboxToggle('isActive', !isActive)
                            }
                          />
                        )}
                      />
                    </FormGroup>
                  </TableCell>
                </TableRow>
              </TableBody>
            </Table>
          </DialogContent>
          <DialogActions>
            <Grid
              container
              alignItems="flex-end"
              justify="flex-end"
              className="createEditProductCreateRow"
            >
              <Grid item>
                <Button
                  className="ssenseButton ssensePrimary"
                  onClick={this.handleSave}
                >
                  {i18n.t('save')}
                </Button>
              </Grid>
              <Grid item>
                <Button
                  className="ssenseButton ssenseDefault"
                  onClick={this.hideDialog}
                >
                  {i18n.t('cancel')}
                </Button>
              </Grid>
            </Grid>
          </DialogActions>
        </Dialog>
        <ContractInputModal
          ref={this.manageContractsModalRef}
          positions={positions}
          handleSaveContracts={this.handleSaveContracts}
          updateContracts={this.updateContracts}
        />
      </>
    );
  }
}

AddEmployeeModal.propTypes = {
  displaySnackbar: PropTypes.func.isRequired,
  refreshEmployees: PropTypes.func.isRequired,
  user: UserType.isRequired
};

export default AddEmployeeModal;
